import React, { useState, useEffect } from 'react';
import { MessageForm } from '../components/activities/MessageForm';
import { MessageExamples } from '../components/common/MessageExamples';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://v2.schmalgsicht.de';

/**
 * Section 2 - Message Section
 * Allows users to send messages to the radio hosts
 */
export function MessageSection() {
  const [lastMessage, setLastMessage] = useState(null);

  // Fetch latest approved message from API
  useEffect(() => {
    const fetchLastMessage = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/messages`);
        if (response.ok) {
          const data = await response.json();
          if (data.messages && data.messages.length > 0) {
            // Filter only approved messages and get the newest one
            const oneHourAgo = Date.now() - 60 * 60 * 1000;
            const approvedMessages = data.messages.filter(
              msg => msg.approved === true && msg.approved_at && new Date(msg.approved_at).getTime() > oneHourAgo
            );
            if (approvedMessages.length > 0) {
              const newest = approvedMessages[approvedMessages.length - 1];
              setLastMessage(newest);
            }
          }
        }
      } catch (error) {
        // Keep null on error
      }
    };

    fetchLastMessage();
  }, []);
  // Helper function to highlight specific words with gradient
  const highlightText = (text) => {
    if (!text) return null;

    // Split by the phrases we want to highlight (case-insensitive)
    const parts = text.split(/(DEINEM schmalgsicht|schmalgsicht|Kein Problem|Also ran an die Tasten)/gi);

    return parts.map((part, index) => {
      const lowerPart = part.toLowerCase();
      if (
        lowerPart === 'deinem schmalgsicht' ||
        lowerPart === 'schmalgsicht' ||
        lowerPart === 'kein problem' ||
        lowerPart === 'also ran an die tasten'
      ) {
        return (
          <span
            key={index}
            className="font-bold"
            style={{
              background: 'linear-gradient(to right, #3b82f6, #ec4899)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}
          >
            {part}
          </span>
        );
      }
      return <React.Fragment key={index}>{part}</React.Fragment>;
    });
  };

  const description1 = "Damit schmalgsicht zu DEINEM schmalgsicht wird, sind wir noch interaktiver als je zuvor!\nSchreib uns einfach – egal ob Lob, Kritik, Grüße oder deine Meinung zu aktuellen Themen.\nDu willst einen Song hören, der gerade perfekt zu deiner Stimmung passt?\nKein Problem – Wir erfüllen deine Musikwünsche live on air!";

  const description2 = "Also ran an die Tasten:\nSag uns, was dich bewegt, was du hören willst und was im Radio auf keinen Fall fehlen darf.\nDenn deine Nachricht macht unser Programm lebendig.";

  return (
    <div
      id="schreib-uns"
      className="min-h-screen sm:h-screen flex items-start sm:items-center justify-center w-full relative pointer-events-none px-4 sm:px-8 md:px-16 lg:px-24 pt-24 sm:pt-32 pb-24 sm:pb-32"
      style={{
        scrollSnapAlign: 'start',
        scrollSnapStop: 'always',
        overflow: 'hidden',
        maxWidth: '100vw'
      }}
    >
      <div className="max-w-5xl w-full text-center">
        {/* Section Heading */}
        <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-center mb-6 sm:mb-10 text-white">
          Dein Gedanke, unser Mikro
        </h2>

        <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/80 leading-relaxed mb-6 sm:mb-8 max-w-6xl mx-auto px-4" style={{ whiteSpace: 'pre-line' }}>
          {highlightText(description1)}
        </p>

        <div className="pointer-events-auto mt-6 sm:mt-8 w-full max-w-6xl mx-auto">
          <MessageForm />
          <MessageExamples />

          {/* Last Message Display */}
          {lastMessage && (
            <div className="mt-8 sm:mt-10 px-4">
              <p className="text-white/80 text-sm sm:text-base md:text-lg lg:text-xl mb-3 text-center">
                Letzte Nachricht
              </p>
              <div className="bg-white rounded-2xl px-6 py-4 max-w-2xl mx-auto transition-all duration-300">
                <p className="text-gray-800 text-sm font-light text-center" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  "{lastMessage.message}"
                </p>
              </div>
            </div>
          )}

          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/80 leading-relaxed mt-6 sm:mt-8 max-w-6xl mx-auto px-4" style={{ whiteSpace: 'pre-line' }}>
            {highlightText(description2)}
          </p>

          <p className="text-xs sm:text-sm text-white/50 mt-6 sm:mt-8 max-w-6xl mx-auto font-light px-4">
            Bitte denke daran, dass der Inhalt deiner Nachricht den allgemeinen Verhaltensregeln entsprechen muss, um vorgelesen zu werden.
          </p>
        </div>
      </div>
    </div>
  );
}
